import {
  emptyPersonFields,
  FIELD_LIMITS,
  firstNameOf,
  TrackerPersonFields,
} from './tracker-people';

/**
 * Rows of cells from CSV or tab-separated text. Double quotes wrap a cell
 * that holds the delimiter, a newline or a doubled quote. Each line uses
 * tabs when it has one outside quotes, otherwise commas, so rows copied
 * from Excel or Sheets and typed lists both work.
 */
export function parseDelimitedRows(text: string, delimiter?: ',' | '\t'): string[][] {
  const rows: string[][] = [];
  const source = text.replace(/\r\n?/g, '\n');
  let start = 0;
  while (start < source.length) {
    const sep = delimiter ?? lineDelimiter(source, start);
    const cells: string[] = [];
    let cell = '';
    let quoted = false;
    let i = start;
    for (; i < source.length; i++) {
      const ch = source[i];
      if (quoted) {
        if (ch === '"' && source[i + 1] === '"') {
          cell += '"';
          i++;
        } else if (ch === '"') {
          quoted = false;
        } else {
          cell += ch;
        }
      } else if (ch === '"' && cell.trim() === '') {
        quoted = true;
        cell = '';
      } else if (ch === sep) {
        cells.push(cell.trim());
        cell = '';
      } else if (ch === '\n') {
        break;
      } else {
        cell += ch;
      }
    }
    cells.push(cell.trim());
    if (cells.some((c) => c !== '')) {
      rows.push(cells);
    }
    start = i + 1;
  }
  return rows;
}

function lineDelimiter(source: string, start: number): ',' | '\t' {
  let quoted = false;
  for (let i = start; i < source.length; i++) {
    const ch = source[i];
    if (ch === '"') {
      quoted = !quoted;
    } else if (!quoted && ch === '\t') {
      return '\t';
    } else if (!quoted && ch === '\n') {
      break;
    }
  }
  return ',';
}

type PasteColumn = keyof TrackerPersonFields | 'name';

const HEADER_NAMES: Record<string, PasteColumn> = {
  first: 'firstName',
  'first name': 'firstName',
  full: 'fullName',
  'full name': 'fullName',
  name: 'name',
  email: 'email',
  phone: 'phone',
  offer: 'offer',
  segment: 'segment',
  tier: 'tier',
  note: 'note',
  notes: 'note',
};

function headerColumn(cell: string) {
  return HEADER_NAMES[
    cell
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, ' ')
  ];
}

const PHONE_PATTERN = /^\+?[\d\s\-().]+$/;

function isPhone(cell: string) {
  return PHONE_PATTERN.test(cell) && cell.replace(/\D/g, '').length >= 7;
}

/** A one-word name is the first name; a longer one is the full name, and its first word the first name. */
function applyName(fields: TrackerPersonFields, name: string) {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return;
  }
  if (trimmed.includes(' ')) {
    fields.fullName = trimmed;
    fields.firstName = firstNameOf(trimmed);
  } else {
    fields.firstName = trimmed;
  }
}

export function clampFields(fields: TrackerPersonFields): TrackerPersonFields {
  const clamped = { ...fields };
  for (const key of Object.keys(FIELD_LIMITS) as Array<keyof TrackerPersonFields>) {
    clamped[key] = clamped[key].trim().slice(0, FIELD_LIMITS[key]);
  }
  return clamped;
}

export type PasteResult = {
  people: TrackerPersonFields[];
  /** Rows with no name at all, dropped. */
  noName: number;
};

/**
 * People from rows pasted out of a sheet, an email list or notes. A first
 * row of known column names is a header; without one, a cell with "@" is
 * the email, a cell of 7+ digits is the phone, and the first other cell is
 * the name.
 */
export function parsePastedPeople(text: string): PasteResult {
  const rows = parseDelimitedRows(text);
  const people: TrackerPersonFields[] = [];
  let noName = 0;
  if (!rows.length) {
    return { people, noName };
  }
  const headerColumns = rows[0].map(headerColumn);
  const hasHeader = headerColumns.some(Boolean);
  for (const row of hasHeader ? rows.slice(1) : rows) {
    const fields = emptyPersonFields();
    if (hasHeader) {
      row.forEach((cell, i) => {
        const column = headerColumns[i];
        if (!column || !cell) {
          return;
        }
        if (column === 'name') {
          applyName(fields, cell);
        } else {
          fields[column] = cell;
        }
      });
      if (!fields.firstName && fields.fullName) {
        fields.firstName = firstNameOf(fields.fullName);
      }
    } else {
      let named = false;
      const rest: string[] = [];
      for (const cell of row) {
        if (!cell) {
          continue;
        }
        if (!fields.email && cell.includes('@')) {
          fields.email = cell;
        } else if (!fields.phone && isPhone(cell)) {
          fields.phone = cell;
        } else if (!named) {
          applyName(fields, cell);
          named = true;
        } else {
          rest.push(cell);
        }
      }
      if (rest.length) {
        fields.offer = rest[0];
      }
    }
    if (!fields.firstName.trim()) {
      noName++;
      continue;
    }
    people.push(clampFields(fields));
  }
  return { people, noName };
}
